import { Component, EventEmitter, Input, OnDestroy, OnInit, Output , signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject, takeUntil } from 'rxjs';
import { LanguageService } from 'src/app/services/account/language.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { MapService } from 'src/app/services/map/map.service';
import { AccountService } from 'src/app/services/account/account.service';
import { ChargingOperator } from 'src/app/models/charging-operator';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { ModalOptionPanelComponent } from '../../modal-option-panel/modal-option-panel.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { EvLoaderComponent } from '../../../ui-components/ev-loader/ev-loader.component';

export type ChargingOperatorsMode = 'prefer' | 'avoid';

/**
 * Full-page editor for the user's preferred or avoided charging operators.
 * Operators are added through a Material select with an embedded search box
 * (same pattern as the vehicle brand selector). Driven by `mode`.
 */
@Component({
  standalone: true,
  selector: 'app-charging-operators-page',
  templateUrl: './charging-operators-page.component.html',
  styleUrls: ['./charging-operators-page.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, MatFormField, MatSelect, MatOption,
    NgxMatSelectSearchModule, ModalOptionPanelComponent, EvLoaderComponent]
})
export class ChargingOperatorsPageComponent implements OnInit, OnDestroy {
  @Input() mode: ChargingOperatorsMode = 'prefer';

  /** Asks the dialog to return to the charging options page. */
  @Output() back = new EventEmitter<void>();

  /** Operators already chosen for the current mode. */
  public readonly selectedOperators = signal<ChargingOperator[]>([]);
  /** Operators not yet used in either list (selectable in the dropdown). */
  public readonly availableOperators = signal<ChargingOperator[]>([]);
  public readonly filteredOperators = signal<ChargingOperator[]>([]);

  public operatorSelectCtrl = new FormControl<ChargingOperator | null>(null);
  public operatorFilterCtrl = new FormControl('');
  public readonly isLoading = signal<boolean>(false);

  private readonly destroyed = new Subject<void>();

  constructor(
    public languageService: LanguageService,
    public settingsService: SettingsService,
    public mapService: MapService,
    public accountService: AccountService,
    private readonly stackedModalsService: StackedModalsService
  ) {}

  ngOnInit(): void {
    this.refreshOperatorLists();
    this.operatorFilterCtrl.valueChanges.pipe(takeUntil(this.destroyed)).subscribe((search) => {
      this.filterOperators(search ?? '');
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  public get panelTitle(): string {
    return this.mode === 'prefer'
      ? this.languageService.languageJSON?.SettingsDialog_Preferred
      : this.languageService.languageJSON?.SettingsDialog_Avoided;
  }

  /** Adds the operator chosen in the dropdown to the current list. */
  public addOperator(operator: ChargingOperator | null): void {
    if (!operator) return;
    const ids = this.getStoredIds();
    ids.push(operator.id);
    this.storeIds(ids);
    this.operatorSelectCtrl.setValue(null);
    this.operatorFilterCtrl.setValue('');
    this.commit();
  }

  public removeOperator(operator: ChargingOperator): void {
    const dialog = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.SettingsDialog_ReenableTitle,
        message: this.languageService.languageJSON.SettingsDialog_ReenableDesc,
        buttons: [this.languageService.languageJSON.Global_Normal, this.languageService.languageJSON.Global_Cancel]
      }, autoFocus: false
    });
    dialog.afterClosed().subscribe((resp) => {
      if (!resp) return;
      const ids = this.getStoredIds();
      const idx = ids.indexOf(operator.id);
      if (idx !== -1) ids.splice(idx, 1);
      this.storeIds(ids);
      this.commit();
    });
  }

  // ---- helpers ----
  private getStoredIds(): number[] {
    return this.mode === 'prefer'
      ? this.settingsService.getFavoriteChargingOperatorIds()
      : this.settingsService.getDisabledChargingOperatorIds();
  }

  private storeIds(ids: number[]): void {
    if (this.mode === 'prefer') this.settingsService.setFavoriteChargingOperatorIds(ids);
    else this.settingsService.setDisabledChargingOperatorIds(ids);
  }

  /** Persist to the account (when authorized), then rebuild the lists and recalc. */
  private commit(): void {
    this.isLoading.set(true);
    const done = () => {
      this.refreshOperatorLists();
      this.isLoading.set(false);
      this.mapService.setChargingStationRecalc();
    };
    if (this.accountService.getIsAuthorized()) {
      const request$ = this.mode === 'prefer'
        ? this.accountService.setFavoriteChargingOperators()
        : this.accountService.setDisabledChargingOperators();
      request$.subscribe(() => done());
    } else {
      done();
    }
  }

  private refreshOperatorLists(): void {
    const all = this.mapService.chargingOperatorsArray;
    const favoriteIds = this.settingsService.getFavoriteChargingOperatorIds();
    const disabledIds = this.settingsService.getDisabledChargingOperatorIds();

    const selectedIds = this.mode === 'prefer' ? favoriteIds : disabledIds;
    // built locally and assigned once: pushing into the signal's array would notify nothing
    const selected: ChargingOperator[] = [];
    for (const id of selectedIds) {
      const op = this.mapService.getChargingOperatorByID(id);
      if (op) selected.push(op);
    }
    this.selectedOperators.set(selected);

    this.availableOperators.set(all.filter(
      (op) => !favoriteIds.includes(op.id) && !disabledIds.includes(op.id)
    ));
    this.filterOperators(this.operatorFilterCtrl.value ?? '');
  }

  private filterOperators(search: string): void {
    const term = search.trim().toLowerCase();
    this.filteredOperators.set(term
      ? this.availableOperators().filter((op) => op.name.toLowerCase().includes(term))
      : this.availableOperators());
  }
}
